import { useState, useEffect, useRef } from 'react';
import { User, getUsersByOrg, createUser, updateUser, deleteUser } from '../../services/admin';

export default function UsersManager({ orgId }: { orgId: string }) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<User>>({
    username: '',
    email: '',
    role: 'dispatcher',
  });
  const [password, setPassword] = useState('');

  // Skip auto-refresh while the add/edit form is open so it can't clobber in-progress edits.
  const showFormRef = useRef(showForm);
  useEffect(() => {
    showFormRef.current = showForm;
  }, [showForm]);

  useEffect(() => {
    loadUsers();
    const interval = setInterval(() => {
      if (!showFormRef.current) loadUsers(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [orgId]);

  const loadUsers = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getUsersByOrg(orgId);
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!editingId && !password) {
        alert('Password required for new users');
        return;
      }
      
      const userData = editingId ? formData : { ...formData, password };
      
      if (editingId) {
        await updateUser(orgId, editingId, userData);
      } else {
        await createUser(orgId, userData as Omit<User, 'id' | 'created_at' | 'updated_at'>);
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData({ username: '', email: '', role: 'dispatcher' });
      setPassword('');
      await loadUsers();
    } catch (err) {
      console.error('Failed to save user:', err);
    }
  };

  const handleEdit = (user: User) => {
    setEditingId(user.id);
    setFormData(user);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this user? This action cannot be undone.')) {
      try {
        await deleteUser(orgId, id);
        await loadUsers();
      } catch (err) {
        console.error('Failed to delete user:', err);
      }
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-600';
      case 'dispatcher':
        return 'bg-blue-600';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">👥 Users & Accounts</h2>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setFormData({ username: '', email: '', role: 'dispatcher' });
            setPassword('');
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          {showForm ? 'Cancel' : '+ New User'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold mb-4">{editingId ? 'Edit User' : 'Create New User'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                <input
                  type="text"
                  value={formData.username || ''}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="e.g., john.smith"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="e.g., john@company.com"
                  required
                />
              </div>
            </div>
            
            {!editingId && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="Enter password"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
              <select
                value={formData.role || 'dispatcher'}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
              >
                <option value="observer">Observer (view only)</option>
                <option value="dispatcher">Dispatcher (manage channels)</option>
                <option value="admin">Administrator (full access)</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                Save User
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ username: '', email: '', role: 'dispatcher' });
                  setPassword('');
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading users...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
          <p className="text-lg">No users found. Create one to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700 border-b border-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Username</th>
                <th className="px-4 py-3 text-left font-bold">Email</th>
                <th className="px-4 py-3 text-left font-bold">Role</th>
                <th className="px-4 py-3 text-left font-bold">Joined</th>
                <th className="px-4 py-3 text-center font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700 transition">
                  <td className="px-4 py-3">{user.username}</td>
                  <td className="px-4 py-3 text-gray-400">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className={`${getRoleBadgeColor(user.role)} text-white px-2 py-1 rounded text-xs font-bold`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleEdit(user)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-gray-400 text-sm">Total users: {users.length}</p>
    </div>
  );
}
